import { type BiomeId, type Weather, type WeatherKind, weatherOf } from '@shefira/shared';
import type Phaser from 'phaser';

import { followPreference } from '../settings/preferences.js';

/**
 * Under the night's shade (9 000), so the rain darkens with the world, and
 * over every sprite (6 144 at most): the status line, the player's bar and
 * the damage numbers stay above it.
 */
const WEATHER_DEPTH = 8_900;
/** The strength only moves by steps (`WEATHER_STEPS`): reading it once a second is enough. */
const READ_EVERY_MS = 1_000;
/** The most grains on the screen at full strength: one draw call, nothing a phone feels. */
const MAX_GRAINS = 120;

interface FallSpec {
  /** The grain, drawn once: `width` × `height` pixels of `color`. */
  width: number;
  height: number;
  color: number;
  alpha: number;
  /** Pixels per second, on the screen. */
  speedX: readonly [number, number];
  speedY: readonly [number, number];
  /** How long a grain lives, in ms: it fades out over its life. */
  life: readonly [number, number];
}

/** Flat colour laid over the screen: the fog, the sand's haze. */
interface VeilSpec {
  color: number;
  alpha: number;
}

/** Each kind at full strength. */
const FALLS: Partial<Record<WeatherKind, FallSpec>> = {
  rain: {
    width: 1,
    height: 4,
    color: 0xa8c0e0,
    alpha: 0.7,
    speedX: [-40, -30],
    speedY: [280, 340],
    life: [300, 500],
  },
  blizzard: {
    width: 2,
    height: 2,
    color: 0xf4f8ff,
    alpha: 0.9,
    speedX: [140, 200],
    speedY: [50, 90],
    life: [600, 1_000],
  },
  sandstorm: {
    width: 3,
    height: 1,
    color: 0xe8c890,
    alpha: 0.9,
    speedX: [240, 300],
    speedY: [10, 30],
    life: [300, 600],
  },
};

const VEILS: Partial<Record<WeatherKind, VeilSpec>> = {
  fog: { color: 0xc8ccd4, alpha: 0.3 },
  sandstorm: { color: 0xd8b070, alpha: 0.2 },
};

interface Grain {
  bob: Phaser.GameObjects.Bob;
  /** Where it really is: the bob is drawn at the whole pixel. */
  x: number;
  y: number;
  vx: number;
  vy: number;
  age: number;
  life: number;
}

function between([low, high]: readonly [number, number]): number {
  return low + Math.random() * (high - low);
}

function grainTexture(scene: Phaser.Scene, kind: WeatherKind, spec: FallSpec): string {
  const key = `fx.weather.${kind}`;
  if (scene.textures.exists(key)) return key;
  const graphics = scene.make.graphics({}, false);
  graphics.fillStyle(spec.color, 1);
  graphics.fillRect(0, 0, spec.width, spec.height);
  graphics.generateTexture(key, spec.width, spec.height);
  graphics.destroy();
  return key;
}

/**
 * The weather on the open world (ADR 0093): grains of whole pixels (rain,
 * snow, sand) and a flat veil (fog, the sand's haze) held to the screen,
 * never to the map. The grains are bobs of one blitter, which the camera's
 * `roundPixels` puts on whole pixels (Phaser's particles are not rounded).
 * The weather comes from the shared clock (`weatherOf`), the same on the
 * server; this client only shows it and decides nothing.
 */
export class WeatherLayer {
  private readonly scene: Phaser.Scene;
  private readonly veil: Phaser.GameObjects.Rectangle;
  private readonly unfollow: () => void;
  private blitter: Phaser.GameObjects.Blitter | undefined;
  private fall: FallSpec | undefined;
  private readonly grains: Grain[] = [];
  /** How many grains live at once, by the strength. */
  private wanted = 0;
  private enabled = true;
  private biome: BiomeId | undefined;
  /** What is shown, redrawn only when the kind or the strength changes. */
  private shown: Pick<Weather, 'kind' | 'strength'> = { kind: 'clear', strength: 0 };
  private nextReadAt = 0;
  private lastTime: number | undefined;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    this.veil = scene.add
      .rectangle(0, 0, 1, 1, 0xffffff)
      .setOrigin(0, 0)
      .setScrollFactor(0)
      .setDepth(WEATHER_DEPTH)
      .setVisible(false);
    this.unfollow = followPreference('nightAndWeather', (enabled) => {
      this.enabled = enabled;
      this.nextReadAt = 0;
    });
  }

  /** The biome the player stands in; `undefined` outside any. */
  setBiome(biome: BiomeId | undefined): void {
    if (biome === this.biome) return;
    this.biome = biome;
    this.nextReadAt = 0;
  }

  /** Once a frame, `time` on the scene's clock. */
  update(time: number): void {
    const elapsed = this.lastTime === undefined ? 0 : Math.min(100, time - this.lastTime);
    this.lastTime = time;
    if (time >= this.nextReadAt) {
      this.nextReadAt = time + READ_EVERY_MS;
      this.read();
    }
    if (this.fall !== undefined) this.move(this.fall, elapsed);
  }

  private read(): void {
    const weather =
      this.enabled && this.biome !== undefined
        ? weatherOf(this.biome, Date.now())
        : { kind: 'clear' as const, strength: 0 };
    const { width, height } = this.scene.cameras.main;
    // The veil follows a resized screen too.
    const veil = weather.kind === 'clear' ? undefined : VEILS[weather.kind];
    this.veil.setVisible(veil !== undefined);
    if (veil !== undefined) {
      this.veil.setSize(width, height);
      this.veil.setFillStyle(veil.color, veil.alpha * weather.strength);
    }
    if (weather.kind === this.shown.kind && weather.strength === this.shown.strength) return;
    if (weather.kind !== this.shown.kind) this.swapFall(weather.kind);
    this.shown = { kind: weather.kind, strength: weather.strength };
    this.wanted = Math.round(MAX_GRAINS * weather.strength);
  }

  private swapFall(kind: Weather['kind']): void {
    this.blitter?.destroy();
    this.blitter = undefined;
    this.grains.length = 0;
    this.fall = kind === 'clear' ? undefined : FALLS[kind];
    if (kind === 'clear' || this.fall === undefined) return;
    this.blitter = this.scene.add
      .blitter(0, 0, grainTexture(this.scene, kind, this.fall))
      .setScrollFactor(0)
      .setDepth(WEATHER_DEPTH);
  }

  /** Ages and moves the grains; a dead one is born again somewhere on the screen. */
  private move(fall: FallSpec, elapsed: number): void {
    const blitter = this.blitter;
    if (blitter === undefined) return;
    while (this.grains.length < this.wanted) {
      const grain: Grain = {
        bob: blitter.create(0, 0),
        x: 0,
        y: 0,
        vx: 0,
        vy: 0,
        age: 0,
        life: 0,
      };
      this.reborn(fall, grain);
      // Spread the first ones over their life, so they do not all fade together.
      grain.age = Math.random() * grain.life;
      this.grains.push(grain);
    }
    while (this.grains.length > this.wanted) this.grains.pop()?.bob.destroy();
    const seconds = elapsed / 1_000;
    for (const grain of this.grains) {
      grain.age += elapsed;
      if (grain.age >= grain.life) this.reborn(fall, grain);
      grain.x += grain.vx * seconds;
      grain.y += grain.vy * seconds;
      grain.bob.x = Math.round(grain.x);
      grain.bob.y = Math.round(grain.y);
      grain.bob.setAlpha(fall.alpha * (1 - grain.age / grain.life));
    }
  }

  /** Born anywhere on the screen and a margin up and against the wind, so the edges fill too. */
  private reborn(fall: FallSpec, grain: Grain): void {
    const { width, height } = this.scene.cameras.main;
    grain.vx = between(fall.speedX);
    grain.vy = between(fall.speedY);
    grain.life = between(fall.life);
    grain.age = 0;
    const driftX = grain.vx * (grain.life / 1_000);
    const driftY = grain.vy * (grain.life / 1_000);
    grain.x = Math.min(0, -driftX) + Math.random() * (width + Math.abs(driftX));
    grain.y = -driftY + Math.random() * (height + driftY);
  }

  destroy(): void {
    this.unfollow();
    this.blitter?.destroy();
    this.veil.destroy();
  }
}
